import { API_BASE_URL } from '../lib/constants';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  MdOutlineAssignment,
  MdOutlineDescription,
  MdOutlineHealthAndSafety,
  MdOutlineLocalHospital,
  MdOutlineMedicalServices,
  MdKeyboardArrowRight,
  MdOutlineHealing,
  MdCheckCircle,
  MdRadioButtonUnchecked,
  MdEdit
} from 'react-icons/md';
import axios from 'axios';

export default function PostOperative() {
  const uhid_no = useParams().uhid_no;
  
  // State to track form completion status
  const [formStatus, setFormStatus] = useState({
    postOperativeOrders: 'not-started',
    postOperativeInstructions: 'not-started', 
    postAnaesthesiaRecovery: 'not-started',
    analgesiaAssessment: 'not-started',
    postOperativeHandover: 'not-started'
  });
  
  const [loading, setLoading] = useState(true);

  // Check all form statuses when component mounts
  useEffect(() => {
    checkAllFormStatuses();
  }, [uhid_no]);

  // Function to check all form statuses
  const checkAllFormStatuses = async () => {
    try {
      setLoading(true);
      
      // Call all your existing GET APIs to check if data exists
      const formChecks = await Promise.allSettled([
        // Replace these with your actual API endpoints
  axios.get(`${API_BASE_URL}/get_post_operative_order/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_post_op_instruction/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_alderete_score/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_assessment/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_post_op_handover/${uhid_no}`)
      ]);

      // Check which forms have data
      const newStatus = {
        postOperativeOrders: getFormStatus(formChecks[0]),
        postOperativeInstructions: getFormStatus(formChecks[1]),
        postAnaesthesiaRecovery: getFormStatus(formChecks[2]),
        analgesiaAssessment: getFormStatus(formChecks[3]),
        postOperativeHandover: getFormStatus(formChecks[4])
      };

      setFormStatus(newStatus);
      setLoading(false);
    } catch (error) {
      console.error('Error checking form statuses:', error);
      setLoading(false);
    }
  };

  // Helper function to determine form status
  const getFormStatus = (apiResponse) => {
    if (apiResponse.status === 'fulfilled') {
      const data = apiResponse.value.data;
      
      // Check if form has data
      if (data && Object.keys(data).length > 0) {
        // Check if form is complete (you can add more logic here)
        // For now, if data exists, it's complete
        return 'complete';
      }
    }
    
    // Check if form is in progress (saved in sessionStorage)
    const savedForm = sessionStorage.getItem(`postOpForm_${uhid_no}`);
    if (savedForm) {
      return 'incomplete';
    }
    
    return 'not-started';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'complete':
        return <MdCheckCircle className="w-5 h-5 text-green-500" />;
      case 'incomplete':
        return <MdEdit className="w-5 h-5 text-yellow-500" />;
      default:
        return <MdRadioButtonUnchecked className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'complete':
        return 'Completed - Click to Edit';
      case 'incomplete':
        return 'In Progress - Continue';
      default:
        return 'Not Started';
    }
  };

  const getButtonStyle = (status) => {
    const baseStyle = "w-full py-8 text-lg flex items-center justify-between px-6 transition-all duration-200";
    
    switch(status) {
      case 'complete':
        return `${baseStyle} bg-green-600 hover:bg-green-500 text-white border-2 border-green-400`;
      case 'incomplete':
        return `${baseStyle} bg-yellow-600 hover:bg-yellow-500 text-white border-2 border-yellow-400`;
      default:
        return `${baseStyle} bg-teal-700 hover:bg-teal-600 text-white`;
    }
  };

  const formItems = [
    {
      key: 'postOperativeOrders',
      icon: MdOutlineAssignment,
      title: 'Post Operative Orders',
      description: 'Post-surgery medical instructions',
      url: `/intraOperativeOrders/${uhid_no}`,
      status: formStatus.postOperativeOrders
    },
    {
      key: 'postOperativeInstructions',
      icon: MdOutlineDescription,
      title: 'Post Operative Instructions', 
      description: 'Patient care guidelines',
      url: `/postOperativeOrders/${uhid_no}`,
      status: formStatus.postOperativeInstructions
    },
    {
      key: 'postAnaesthesiaRecovery',
      icon: MdOutlineLocalHospital,
      title: 'Post Anaesthesia Recovery Score',
      description: 'Recovery monitoring assessment',
      url: `/AldreteScore/${uhid_no}`,
      status: formStatus.postAnaesthesiaRecovery
    },
    {
      key: 'analgesiaAssessment',
      icon: MdOutlineMedicalServices,
      title: 'Analgesia Assessment Chart',
      description: 'Pain management tracking',
      url: `/postOperativeAssesment/${uhid_no}`,
      status: formStatus.analgesiaAssessment
    },
    {
      key: 'postOperativeHandover',
      icon: MdOutlineHealthAndSafety,
      title: 'Post Operative Handover',
      description: 'Post-surgery evaluation',
      url: `/postOperativeForm2/${uhid_no}`,
      status: formStatus.postOperativeHandover
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-teal-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-teal-600">Loading form statuses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-teal-50">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-lg">
        <div className="flex flex-col items-center space-y-4">
          <MdOutlineHealing className="w-20 h-20 text-teal-600" />
          <h1 className="text-2xl font-bold text-center text-teal-800">
            Post Operative
          </h1>
          <p className="text-sm text-gray-600 text-center">
            UHID: {uhid_no}
          </p>
        </div>

        {/* Progress Summary */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className="text-sm text-gray-600">
              {Object.values(formStatus).filter(status => status === 'complete').length}/
              {Object.values(formStatus).length} Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-teal-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${(Object.values(formStatus).filter(status => status === 'complete').length / Object.values(formStatus).length) * 100}%`
              }}
            ></div>
          </div>
        </div>

        <div className="flex flex-col space-y-4">
          {formItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <Button
                key={item.key}
                onClick={() => window.location.href = item.url}
                className={getButtonStyle(item.status)}
                variant="default"
              >
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <IconComponent className="w-6 h-6" />
                    <div className="absolute -top-1 -right-1">
                      {getStatusIcon(item.status)}
                    </div>
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{item.title}</span>
                    <span className="text-xs opacity-90">{item.description}</span>
                    <span className="text-xs font-medium mt-1 opacity-75">
                      {getStatusText(item.status)}
                    </span>
                  </div>
                </div>
                <MdKeyboardArrowRight className="w-5 h-5" />
              </Button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="bg-gray-50 p-3 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Status Legend:</h3>
          <div className="flex flex-col space-y-1 text-xs">
            <div className="flex items-center space-x-2">
              <MdCheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-gray-600">Completed (Click to edit)</span>
            </div>
            <div className="flex items-center space-x-2">
              <MdEdit className="w-4 h-4 text-yellow-500" />
              <span className="text-gray-600">In Progress (Continue form)</span>
            </div>
            <div className="flex items-center space-x-2">
              <MdRadioButtonUnchecked className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Not Started</span>
            </div>
          </div>
        </div>

        {/* Refresh Button */}
        <Button
          onClick={checkAllFormStatuses}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white"
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Status'}
        </Button>
      </div>
    </div>
  );
}

